// istanbul ignore file
// https://distribusion.atlassian.net/browse/USER-339
import { AuthProvider, useAuthContext, useAuth, useUser } from '@distribusion/owl-auth'
import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react'

interface User {
  id: string
  first_name: string
  last_name: string
  date_of_birth: string | null
  email: string
  created_at: string
  marketing_consent: boolean
  activation_status: UserActivationStatus
}

interface UserAuthContextValue {
  isInitializing: boolean
  isAuthenticated: boolean
  user: User | null
  restoreSession: (forceRefresh?: boolean) => Promise<boolean>
  createSession: (params: { email: string; password: string }) => Promise<any>
  setUser: React.Dispatch<React.SetStateAction<User | null>>
  setIsAuthenticated: React.Dispatch<React.SetStateAction<boolean>>
  isUserActive: boolean
}

const USER_ACTIVATION_STATUS = {
  UNSPECIFIED: 'USER_STATE_UNSPECIFIED',
  ACTIVE: 'USER_STATE_ACTIVE',
  INACTIVE: 'USER_STATE_INACTIVE',
  DELETED: 'USER_STATE_DELETED',
  LOCKED: 'USER_STATE_LOCKED',
  SUSPENDED: 'USER_STATE_SUSPEND',
  INITIAL: 'USER_STATE_INITIAL',
} as const

type UserActivationStatus = (typeof USER_ACTIVATION_STATUS)[keyof typeof USER_ACTIVATION_STATUS]

const UserAuthContext = createContext<UserAuthContextValue>({
  isInitializing: true,
  isAuthenticated: false,
  user: null,
  isUserActive: false,
  restoreSession: async () => false,
  createSession: async () => ({}),
  setUser: () => {},
  setIsAuthenticated: () => {},
})

export const useUserAuth = (): UserAuthContextValue => useContext(UserAuthContext)

declare global {
  interface Window {
    owlCreateSession?: (params: { email: string; password: string }) => Promise<any>
  }
}

interface UserAuthProviderProps {
  children: ReactNode
}

export const UserAuthProvider: React.FC<UserAuthProviderProps> = ({ children }) => {
  const [isInitializing, setIsInitializing] = useState(true)
  return (
    <AuthProvider>
      <UserAuthProviderInner isInitializing={isInitializing} setIsInitializing={setIsInitializing}>
        {children}
      </UserAuthProviderInner>
    </AuthProvider>
  )
}

interface UserAuthProviderInnerProps {
  children: ReactNode
  isInitializing: boolean
  setIsInitializing: React.Dispatch<React.SetStateAction<boolean>>
}

const UserAuthProviderInner: React.FC<UserAuthProviderInnerProps> = ({
  children,
  isInitializing,
  setIsInitializing,
}) => {
  const { sessionId, setSessionId } = useAuthContext()
  const { handleAuth } = useAuth()
  const { getUser } = useUser()
  const [user, setUser] = useState<User | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  const restoreSession = useCallback(
    async (forceRefresh = false) => {
      const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true'
      if (!sessionId && isLoggedIn) {
        setSessionId('logged-in')
        return true
      }
      if (sessionId && (!user || forceRefresh)) {
        try {
          const userData = (await getUser()) as unknown as User
          setUser(userData)
          setIsAuthenticated(true)
          localStorage.setItem('isLoggedIn', 'true')
          return true
        } catch {
          return false
        }
      }
      return !!user
    },
    [sessionId, setSessionId, user, getUser],
  )

  const createSession = useCallback(
    async ({ email, password }: { email: string; password: string }) => {
      const session = await handleAuth({ email, password })
      setSessionId(session.sessionId)
      try {
        const userData = (await getUser()) as unknown as User
        setUser(userData)
        setIsAuthenticated(true)
        localStorage.setItem('isLoggedIn', 'true')
      } catch (error) {
        setIsAuthenticated(false)
        setUser(null)
        setSessionId('')
        throw new Error('Authentication succeeded but fetching user data failed')
      }
      return session
    },
    [handleAuth, setSessionId, getUser],
  )

  const isUserActive = user?.activation_status === USER_ACTIVATION_STATUS.ACTIVE

  useEffect(() => {
    if (!sessionId) return
    async function load() {
      try {
        const userData = (await getUser()) as unknown as User
        setUser(userData)
        setIsAuthenticated(true)
      } catch {
        setIsAuthenticated(false)
        setUser(null)
        localStorage.removeItem('isLoggedIn')
        setSessionId('')
      }
    }
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId])

  useEffect(() => {
    async function init() {
      await restoreSession()
      window.owlCreateSession = createSession
      setIsInitializing(false)
    }
    init()
  }, [createSession, restoreSession])

  const contextValue: UserAuthContextValue = {
    isInitializing,
    isAuthenticated,
    user,
    restoreSession,
    createSession,
    setUser,
    setIsAuthenticated,
    isUserActive,
  }

  return <UserAuthContext.Provider value={contextValue}>{children}</UserAuthContext.Provider>
}

export default UserAuthProvider
